import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import type {
  CreateFoldersParams,
  DndParams,
  FileType,
  FolderType,
  GetFoldersParams,
  RequestParamsMultipleItems,
  RootDataType,
  UpdateFoldersParams,
} from './cloud-types';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';

const archiveFileAsync = createAsyncThunk<{ success: boolean }, string, { rejectValue: ErrorType }>(
  'archiveFileAsync',
  async (id, { rejectWithValue }) => {
    try {
      return await api.cloud.archiveFile(id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

const updateFileAsync = createAsyncThunk<
  { data: FileType },
  { id: string; name: string; folder_id: string },
  { rejectValue: ErrorType }
>('updateFileAsync', async (data, { rejectWithValue }) => {
  try {
    return await api.cloud.updateFile(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

const getItemsAsync = createAsyncThunk<RootDataType, GetFoldersParams, { rejectValue: ErrorType }>(
  'getItemsAsync',
  async (data, { rejectWithValue }) => {
    try {
      return await api.cloud.getItems(data, data.tab);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      return rejectWithValue(axiosError.data);
    }
  },
);

const getSearchDropdownAsync = createAsyncThunk<
  RootDataType,
  Omit<GetFoldersParams, 'signal' | 'parent_id' | 'tab'>,
  { rejectValue: ErrorType }
>('getSearchDropdownAsync', async (data, { rejectWithValue }) => {
  try {
    return await api.cloud.getSearch(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

// --------------------- folders ------------------------------

const updateFolderAsync = createAsyncThunk<
  { data: FolderType },
  UpdateFoldersParams,
  { rejectValue: ErrorType }
>('updateFolderAsync', async (data, { rejectWithValue }) => {
  try {
    return await api.cloud.updateFolder(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

const archiveFolderAsync = createAsyncThunk<{ success: boolean }, string, { rejectValue: ErrorType }>(
  'archiveFolderAsync',
  async (id, { rejectWithValue }) => {
    try {
      return await api.cloud.archiveFolder(id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

const createFolderAsync = createAsyncThunk<
  { data: FolderType },
  CreateFoldersParams,
  { rejectValue: ErrorType }
>('crateFolderAsync', async (data, { rejectWithValue }) => {
  try {
    return await api.cloud.createFolder(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

const dndAsync = createAsyncThunk<{ data: FolderType }, DndParams, { rejectValue: ErrorType }>(
  'dndAsync',
  async (data, { rejectWithValue }) => {
    try {
      return await api.cloud.dnd(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

const getFolderAsync = createAsyncThunk<RootDataType, string, { rejectValue: ErrorType }>(
  'getFolderAsync',
  async (id, { rejectWithValue }) => {
    try {
      return await api.cloud.getFolder(id);
    } catch (e: any) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

const getFileAsync = createAsyncThunk<{ data: FileType }, string, { rejectValue: ErrorType }>(
  'getFileAsync',
  async (id, { rejectWithValue }) => {
    try {
      return await api.cloud.getFile(id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      return rejectWithValue(axiosError.data);
    }
  },
);

const getFullNameAsync = createAsyncThunk<{ full_name: string }, number, { rejectValue: ErrorType }>(
  'getFullNameAsync',
  async (id, { rejectWithValue }) => {
    try {
      return await api.cloud.getFullName(id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      return rejectWithValue(axiosError.data);
    }
  },
);

const downloadFileAsync = (fileData: { url: string; original_filename: string }) => async () => {
  try {
    const response = await api.cloud.downloadFile(fileData.url).then((url) => {
      return url;
    });

    const blobUrl = URL.createObjectURL(response);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileData.original_filename;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    NotificationService.error('Error downloading file');
  }
};

// ------------------- Move -----------------------

const updateMultiItemsAsync = createAsyncThunk<{}, RequestParamsMultipleItems, { rejectValue: ErrorType }>(
  'updateMultiItemsAsync',
  async (data, { rejectWithValue }) => {
    try {
      return await api.cloud.updateMultiple(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

const removeMultiItemsAsync = createAsyncThunk<{}, RequestParamsMultipleItems, { rejectValue: ErrorType }>(
  'removeMultiItemsAsync',
  async (data, { rejectWithValue }) => {
    try {
      return await api.cloud.removeMultiple(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

const getMoveItemsAsync = createAsyncThunk<
  RootDataType,
  Pick<GetFoldersParams, 'parent_id' | 'last_item_type' | 'last_item_id'>,
  { rejectValue: ErrorType }
>('getItemsAsync', async (data, { rejectWithValue }) => {
  try {
    return await api.cloud.getMoveItems(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const CLOUD_THUNKS = {
  getItemsAsync,
  updateFolderAsync,
  createFolderAsync,
  archiveFolderAsync,
  dndAsync,
  archiveFileAsync,
  updateFileAsync,
  getFolderAsync,
  getFileAsync,
  downloadFileAsync,
  getSearchDropdownAsync,
  getFullNameAsync,
  getMoveItemsAsync,
  removeMultiItemsAsync,
  updateMultiItemsAsync,
};
